// Parts mega-menu flyout. Hover/focus on a system in the left column reveals
// its panel in the right column instantly. The vertical-column layout means
// the mouse moves left→right toward the panel, not diagonally across rows,
// so we don't need a hover-intent debounce.
class PartsFlyout extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-panel-trigger]'));
    this.panels = new Map();
    this.querySelectorAll('.parts-flyout__panel').forEach((p) => this.panels.set(p.id, p));
    this.panelsContainer = this.querySelector('.parts-flyout__panels');

    this.activeId = this.triggers[0]?.dataset.panelTrigger || null;

    this.triggers.forEach((trigger) => {
      const targetId = trigger.dataset.panelTrigger;
      trigger.addEventListener('mouseenter', () => this.activate(targetId));
      trigger.addEventListener('focus', () => this.activate(targetId));
      trigger.addEventListener('keydown', (e) => this.onKeyDown(e));
    });

    this._onResize = () => {
      if (this.activeId) this.alignPanelToTrigger(this.activeId);
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  activate(id) {
    if (!this.panels.has(id) || id === this.activeId) return;
    this.activeId = id;
    this.triggers.forEach((trigger) => {
      const isActive = trigger.dataset.panelTrigger === id;
      if (isActive) trigger.setAttribute('data-active', '');
      else trigger.removeAttribute('data-active');
    });
    this.panels.forEach((panel, panelId) => {
      if (panelId === id) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
    this.alignPanelToTrigger(id);
  }

  // Position the active panel so its top sits roughly opposite the trigger's
  // row in the systems column. Without this, hovering a bottom system pops
  // the panel up to the top of the right column — forcing the user to look
  // (or scroll) up. Clamped so the panel never extends past the container's
  // bottom edge. CSS overrides transform on tablet, so this is a no-op there.
  alignPanelToTrigger(id) {
    const panel = this.panels.get(id);
    const trigger = this.triggers.find((t) => t.dataset.panelTrigger === id);
    const container = this.panelsContainer;
    if (!panel || !trigger || !container) return;

    panel.style.setProperty('--panel-offset-y', '0px');

    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const containerHeight = container.clientHeight;
    const panelHeight = panel.offsetHeight;

    const desired = triggerRect.top - containerRect.top;
    const maxOffset = Math.max(0, containerHeight - panelHeight);
    const clamped = Math.max(0, Math.min(desired, maxOffset));

    panel.style.setProperty('--panel-offset-y', `${clamped}px`);
  }

  onKeyDown(event) {
    const idx = this.triggers.indexOf(event.currentTarget);
    if (idx === -1) return;

    let nextIdx = -1;
    if (event.key === 'ArrowDown') nextIdx = (idx + 1) % this.triggers.length;
    else if (event.key === 'ArrowUp') nextIdx = (idx - 1 + this.triggers.length) % this.triggers.length;
    else if (event.key === 'Home') nextIdx = 0;
    else if (event.key === 'End') nextIdx = this.triggers.length - 1;
    else if (event.key === 'ArrowRight') {
      // Move focus into the active panel's first link
      const panel = this.panels.get(this.activeId);
      const firstLink = panel?.querySelector('a');
      if (firstLink) {
        event.preventDefault();
        firstLink.focus();
      }
      return;
    } else {
      return;
    }

    event.preventDefault();
    this.triggers[nextIdx].focus();
  }
}

if (!customElements.get('parts-flyout')) {
  customElements.define('parts-flyout', PartsFlyout);
}
